import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RatesService } from './rates.service';
import { UserService } from '../../../auth/user.service';
import {
	AlarisComplexTableComponent,
	AlarisDialogService,
	AlarisFilterManagerComponent,
	AlarisLocalStorageService,
	AlarisTableStateService,
	EditPanelWidth,
	RowActionSimple,
	TABLE_STATE_SERVICE_INJECTOR
} from '@campaign-portal/components-library';
import { HlrRates } from '@helpers/types/models';
import { RatesFieldsService } from './rates-fields.service';
import { TableSettingsService } from '@helpers/api/table-settings.service';
import { TableTypes } from '@campaign-portal/namespace/entities/table-settings/specs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { STORAGE } from '@helpers/types/consts';
import { Paging } from '@campaign-portal/namespace/common/rpc.params';
import { EditRateComponent } from './edit-rate/edit-rates.component';
import { RatesDialogComponent } from './dialog/dialog.component';
import { CurrenciesService } from '../currencies/currencies.service';

@Component({
	selector: 'app-rates',
	templateUrl: './rates.component.html',
	styleUrls: [
		// eslint-disable-next-line max-len
		'../../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss',
		'./rates.component.scss'
	],
	providers: [
		AlarisTableStateService,
		{ provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService }
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class RatesComponent extends AlarisComplexTableComponent<HlrRates> {
	override readonly rowActions: RowActionSimple<any>[] = [
		{
			icon: 'icon-edit',
			label: 'actions.edit',
			action: this.edit.bind(this),
			disabled: (): boolean => {
				return !this.user.allowed(['rates.edit']);
			}
		},
		{
			icon: 'icon-delete',
			label: 'actions.delete',
			action: this.delete.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => {
				return !this.user.allowed(['rates.edit']);
			}
		}
	];
	override readonly dblClickRowAction = this.edit;
	override pageSize = parseInt(this.storage.get(STORAGE.PAGE_SIZE) ?? '25', 10);
	override readonly pageSizeOptions = [25, 50, 100, 200];
	override readonly paging: Paging = {
		Skip: this.activePage,
		Take: this.pageSize
	};

	constructor(
		public readonly user: UserService,
		dataService: RatesService,
		public override readonly fieldsService: RatesFieldsService,
		tableSettingsService: TableSettingsService,
		private readonly storage: AlarisLocalStorageService,
		private readonly dialog: AlarisDialogService,
		public readonly currenciesService: CurrenciesService
	) {
		super(dataService, fieldsService, tableSettingsService, TableTypes.INTERNAL);
		this.currenciesService.read().pipe(takeUntilDestroyed()).subscribe();
		this.currenciesService
			.getSystemCurrency()
			.pipe(takeUntilDestroyed())
			.subscribe((res) => {
				if (res) {
					this.applyHeaders(this.fieldsService.headers);
				}
			});
	}

	edit(rate?: HlrRates): void {
		this.editPanel
			.open<
				EditRateComponent,
				{
					client: HlrRates;
				},
				boolean
			>(EditRateComponent, EditPanelWidth.SM, { rate })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.load();
				}
			});
	}

	delete(rate: HlrRates): void {
		this.dialog
			.open<boolean>(RatesDialogComponent, { autoFocus: false, data: { rate } })
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result) {
					this.load();
				}
			});
	}

	getDate(date: string | null): string {
		const newDate = new Date(date!.toString());
		return newDate.toLocaleDateString();
	}

	override editTableFilter(): void {
		this.editPanel.open(AlarisFilterManagerComponent, EditPanelWidth.MD, {
			filtersChange: this.filtersChange,
			filters: this.state.filters,
			tHeaders: this.tHeaders()
		});
	}
}
